import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, BookOpen, FileText, Zap, Globe2, Database, Trophy, Users, Star, Shield, Clock, ArrowRight, Phone, Mail, User, Loader2, LogIn } from "lucide-react";

const API = window.location.origin;

// ── Color palette cycles for dynamic exam categories ──────────────────────────
const EXAM_COLORS = [
  { bg: "bg-indigo-500/15", border: "border-indigo-500/30", text: "text-indigo-300", dot: "bg-indigo-500" },
  { bg: "bg-emerald-500/15", border: "border-emerald-500/30", text: "text-emerald-300", dot: "bg-emerald-500" },
  { bg: "bg-amber-500/15", border: "border-amber-500/30", text: "text-amber-300", dot: "bg-amber-500" },
  { bg: "bg-rose-500/15", border: "border-rose-500/30", text: "text-rose-300", dot: "bg-rose-500" },
  { bg: "bg-cyan-500/15", border: "border-cyan-500/30", text: "text-cyan-300", dot: "bg-cyan-500" },
  { bg: "bg-purple-500/15", border: "border-purple-500/30", text: "text-purple-300", dot: "bg-purple-500" },
];

const BENEFITS = [
  { icon: BookOpen, title: "NCERT-based Study Notes", desc: "Detailed notes on every topic aligned with NCERT curriculum", color: "from-indigo-600 to-blue-600" },
  { icon: FileText, title: "Unlimited Descriptive Practice", desc: "Handwritten subjective answers — get AI-powered evaluation & feedback", color: "from-purple-600 to-indigo-600" },
  { icon: Zap, title: "Unlimited Objective Questions", desc: "5000+ MCQs with expert explanations for rapid revision", color: "from-emerald-600 to-teal-600" },
  { icon: Database, title: "Complete Question Bank + PYQs", desc: "All Previous Year Papers tagged by year and section", color: "from-amber-600 to-orange-600" },
  { icon: Trophy, title: "Print Notes as PDF", desc: "Download & print any topic's notes offline for revision", color: "from-rose-600 to-pink-600" },
  { icon: Globe2, title: "100% Bilingual Content", desc: "Every word available in English and हिंदी simultaneously", color: "from-cyan-600 to-blue-600" },
];

// ── Floating particle background ─────────────────────────────────────────────
const Particles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(18)].map((_, i) => (
      <div
        key={i}
        className="absolute rounded-full opacity-10 animate-pulse"
        style={{
          width: `${Math.random() * 6 + 2}px`,
          height: `${Math.random() * 6 + 2}px`,
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          background: i % 3 === 0 ? "#6366f1" : i % 3 === 1 ? "#f59e0b" : "#10b981",
          animationDelay: `${Math.random() * 4}s`,
          animationDuration: `${Math.random() * 4 + 3}s`,
        }}
      />
    ))}
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
export default function LandingPromo({ onLogin }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", mobile: "", email: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const formRef = useRef(null);

  // Dynamic exam/subject data
  const [exams, setExams] = useState([]);
  const [examsLoading, setExamsLoading] = useState(true);
  const [totalSubjects, setTotalSubjects] = useState(0);

  // Preload Razorpay script + fetch available exams
  useEffect(() => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    document.body.appendChild(s);

    // Fetch dynamic exams
    axios.get(`${API}/api/notes/lms/public/enroll-exams`)
      .then(({ data }) => {
        setExams(data.exams || []);
        const count = (data.exams || []).reduce((acc, e) => acc + e.subjects.length, 0);
        setTotalSubjects(count);
      })
      .catch(() => {}) // Silently fail — UI falls back gracefully
      .finally(() => setExamsLoading(false));

    return () => document.body.removeChild(s);
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = "Enter your full name";
    if (!/^\d{10}$/.test(form.mobile.replace(/\s/g, ""))) e.mobile = "Enter a valid 10-digit mobile number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!window.Razorpay) { toast.error("Payment gateway not loaded. Please refresh."); return; }
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/api/enrollment/initiate`, {
        name: form.name.trim(),
        mobile: form.mobile.replace(/\s/g, ""),
        email: form.email.trim().toLowerCase(),
      });

      const options = {
        key: data.key,
        amount: data.amount,
        currency: data.currency,
        name: "Descriptive Test",
        description: "Premium Enrollment — Lifetime Access",
        order_id: data.order_id,
        image: "/logo.png",
        handler: async (response) => {
          try {
            const verify = await axios.post(`${API}/api/enrollment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              user_id: data.user_id,
              is_new_user: data.is_new_user,
            });
            if (verify.data.success && verify.data.user) {
              const userData = verify.data.user;
              localStorage.setItem('user', JSON.stringify(userData));
              localStorage.setItem('session_token', userData.session_token);
              toast.success("Enrolled! Logging you in...");
              if (onLogin) onLogin(userData);
              navigate('/category');
            } else {
              setEnrolled(true);
              toast.success("Enrollment confirmed! Check your email.");
            }
          } catch (err) {
            toast.error(err.response?.data?.detail || "Verification failed. Contact support.");
          }
          setLoading(false);
        },
        prefill: { name: data.name, email: data.email, contact: data.mobile },
        notes: { user_id: data.user_id },
        theme: { color: "#6366f1" },
        modal: { ondismiss: () => setLoading(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (msg === "already_enrolled") {
        toast.info("You're already enrolled! Login to access your account.");
      } else {
        toast.error(msg || "Something went wrong. Please try again.");
      }
      setLoading(false);
    }
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <div className="min-h-screen bg-[#030712] text-white overflow-x-hidden" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <Helmet>
        <title>Join Descriptive Test - EMRS KVS NVS TGT PGT PRT Exam Preparation 2026</title>
        <meta name="description" content="Enroll in India's best EMRS, KVS, NVS TGT PGT PRT Tier-2 exam preparation platform. AI-powered descriptive test evaluation, 5000+ MCQs, NCERT study notes, mock tests. 100% CBSE syllabus coverage. Bilingual content in English and Hindi." />
        <meta name="keywords" content="join Descriptive Test, EMRS preparation enrollment, KVS TGT PGT enrollment, NVS exam preparation, EMRS tier 2 preparation, KVS tier 2 exam preparation, teaching exam preparation platform, EMRS mock test, descriptive test practice, MCQ practice TGT PGT, NCERT notes for TGT PGT" />
        <meta property="og:title" content="Join Descriptive Test - EMRS KVS NVS Exam Preparation 2026" />
        <meta property="og:description" content="India's #1 TGT PGT PRT exam preparation platform. AI-powered evaluation, 5000+ MCQs, complete syllabus coverage." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://descriptivetest.com/enroll" />
      </Helmet>

      {/* ── Background grid ─────────────────────────────────────── */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(99,102,241,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.04) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />

      {/* ── Navbar ────────────────────────────────────────────────── */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5 border-b border-white/5 backdrop-blur-sm bg-[#030712]/70">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
            <BookOpen className="w-4.5 h-4.5 text-white" size={18} />
          </div>
          <span className="text-lg font-black text-white tracking-tight">Descriptive Test</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="text-white font-bold text-base border border-white/20 hover:border-white/50 hover:bg-white/10 px-5 py-2 rounded-full transition-all">
            Enrolled Students? Login →
          </Link>
          <button
            onClick={scrollToForm}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white text-sm font-bold px-5 py-2 rounded-full transition-all hover:shadow-lg hover:shadow-indigo-500/25 hover:-translate-y-0.5"
          >
            Enroll ₹499
          </button>
        </div>
      </nav>

      {/* ── Main Grid ─────────────────────────────────────────────── */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-12 lg:py-20 flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">

        {/* LEFT — Content (appears below the card on mobile) */}
        <div className="flex-1 min-w-0 order-last lg:order-first">

          {/* Headline */}
          <div className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full px-4 py-1.5 mb-8">
            <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
            <span className="text-indigo-300 text-xs font-bold tracking-widest uppercase">India's #1 TGT / PGT Prep Platform</span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-none tracking-tight mb-4">
            <span className="block text-white">Descriptive</span>
            <span
              className="block"
              style={{ background: "linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #f59e0b 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
            >
              Test
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-slate-300 font-semibold mb-2">
            100% Syllabus Coverage
          </p>
          <p className="text-slate-500 text-base mb-10">
            Prepare smarter for EMRS • KVS • NVS • DSSSB exams — CBSE Based
          </p>

          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[
              { value: "CBSE", label: "Official Syllabus" },
              { value: "5,000+", label: "Practice Questions" },
              { value: totalSubjects > 0 ? `${totalSubjects}+` : "17+", label: "Subjects Covered" },
              { value: exams.length > 0 ? `${exams.length}` : "5+", label: "Exams Covered" },
            ].map((s, i) => (
              <div key={i} className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-4 text-center hover:border-indigo-500/30 transition-colors">
                <div className="text-2xl font-black text-indigo-400 mb-0.5">{s.value}</div>
                <div className="text-xs text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Subjects */}
          <div className="mb-12">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <span className="w-1 h-5 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full inline-block" />
              Available Exams &amp; Subjects
            </h2>
            {examsLoading ? (
              <div className="flex items-center gap-3 text-slate-500 py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading available exams...</span>
              </div>
            ) : exams.length === 0 ? (
              <p className="text-slate-500 text-sm">No exams available yet. Check back soon!</p>
            ) : (
              <div className="space-y-5">
                {exams.map((exam, idx) => {
                  const c = EXAM_COLORS[idx % EXAM_COLORS.length];
                  return (
                    <div key={exam.id} data-testid={`exam-section-${exam.id}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-2 h-2 rounded-full ${c.dot}`} />
                        <span className={`text-sm font-bold uppercase tracking-wider ${c.text}`}>{exam.name}</span>
                      </div>
                      {exam.subjects.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {exam.subjects.map((s) => (
                            <span
                              key={s}
                              className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-all hover:scale-105 cursor-default ${c.bg} ${c.border} ${c.text}`}
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-600 italic">Subjects coming soon...</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Benefits */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <span className="w-1 h-5 bg-gradient-to-b from-emerald-500 to-teal-500 rounded-full inline-block" />
              What You Get
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {BENEFITS.map((b, i) => (
                <div
                  key={i}
                  className="flex gap-4 p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.12] transition-all group"
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${b.color} flex items-center justify-center shrink-0 shadow-lg group-hover:scale-110 transition-transform`}>
                    <b.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white mb-1">{b.title}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Social proof */}
          <div className="flex items-center gap-3 p-4 bg-emerald-500/5 border border-emerald-500/15 rounded-2xl">
            <div className="flex -space-x-2">
              {["S","R","A","M","K"].map((l, i) => (
                <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 border-2 border-[#030712] flex items-center justify-center text-xs font-bold text-white">{l}</div>
              ))}
            </div>
            <div>
              <p className="text-sm font-bold text-emerald-300">50,000+ students already enrolled</p>
              <p className="text-xs text-slate-500">Join them and start scoring higher today</p>
            </div>
          </div>
        </div>

        {/* RIGHT — Enrollment Card (first on mobile, right on desktop) */}
        <div ref={formRef} className="w-full lg:w-[400px] shrink-0 lg:sticky lg:top-24 order-first lg:order-last">
          <div className="relative">
            {/* Glow */}
            <div className="absolute -inset-1 bg-gradient-to-br from-indigo-500/20 to-purple-600/20 rounded-3xl blur-xl" />
            <div className="relative bg-[#0a0f1e] border border-white/10 rounded-3xl overflow-hidden shadow-2xl">

              {/* Card header */}
              <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800 px-7 pt-7 pb-8 relative overflow-hidden">
                <Particles />
                <div className="relative">
                  <div className="inline-flex items-center gap-1.5 bg-amber-400/20 border border-amber-400/30 rounded-full px-3 py-1 mb-4">
                    <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                    <span className="text-amber-300 text-xs font-bold uppercase tracking-wider">Premium Access</span>
                  </div>
                  <h2 className="text-2xl font-black text-white mb-1">Enroll Now</h2>
                  <p className="text-indigo-200 text-sm">One-time payment. Lifetime access.</p>
                  <div className="flex items-baseline gap-2 mt-4">
                    <span className="text-5xl font-black text-white">₹499</span>
                    <div>
                      <p className="text-indigo-200 text-xs line-through">₹999</p>
                      <p className="text-emerald-300 text-xs font-bold">50% OFF</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Success state */}
              {enrolled ? (
                <div className="p-7 text-center">
                  <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-black text-white mb-2">Enrollment Confirmed!</h3>
                  <p className="text-slate-400 text-sm mb-6">Your login credentials have been sent to <strong className="text-white">{form.email}</strong></p>
                  <Link
                    to="/"
                    className="block w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-3.5 rounded-xl text-center text-sm hover:from-indigo-500 hover:to-purple-500 transition-all"
                  >
                    Login to Your Account →
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleEnroll} className="p-7 space-y-4">

                  {/* Name */}
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="text"
                        placeholder="Rahul Kumar"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="enroll-name"
                        className={`w-full bg-white/[0.04] border ${errors.name ? "border-red-500/60" : "border-white/10"} rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/60 focus:bg-white/[0.06] transition-all`}
                      />
                    </div>
                    {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                  </div>

                  {/* Mobile */}
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Mobile Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="tel"
                        placeholder="9876543210"
                        maxLength={10}
                        value={form.mobile}
                        onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "") })}
                        data-testid="enroll-mobile"
                        className={`w-full bg-white/[0.04] border ${errors.mobile ? "border-red-500/60" : "border-white/10"} rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/60 focus:bg-white/[0.06] transition-all`}
                      />
                    </div>
                    {errors.mobile && <p className="text-red-400 text-xs mt-1">{errors.mobile}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="email"
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="enroll-email"
                        className={`w-full bg-white/[0.04] border ${errors.email ? "border-red-500/60" : "border-white/10"} rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/60 focus:bg-white/[0.06] transition-all`}
                      />
                    </div>
                    {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="enroll-submit-btn"
                    className="w-full relative overflow-hidden bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 disabled:opacity-60 text-white font-black py-4 rounded-xl text-sm transition-all hover:shadow-xl hover:shadow-indigo-500/30 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 group mt-2"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        Pay ₹499 & Enroll Now
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>

                  {/* Already paid? Login link */}
                  <Link
                    to="/"
                    data-testid="paid-student-login-btn"
                    className="w-full flex items-center justify-center gap-2 border border-indigo-500/40 hover:border-indigo-400/70 bg-indigo-500/5 hover:bg-indigo-500/10 text-indigo-300 hover:text-indigo-200 font-bold py-3 rounded-xl text-sm transition-all group"
                  >
                    <LogIn className="w-4 h-4" />
                    Paid Students — Login Here
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </Link>

                  {/* Trust row */}
                  <div className="grid grid-cols-3 gap-2 pt-1">
                    {[
                      { icon: Shield, label: "Secure Payment" },
                      { icon: Clock, label: "Lifetime Access" },
                      { icon: Users, label: "50K+ Students" },
                    ].map(({ icon: Icon, label }, i) => (
                      <div key={i} className="flex flex-col items-center gap-1.5 p-2.5 bg-white/[0.02] border border-white/[0.05] rounded-xl">
                        <Icon className="w-3.5 h-3.5 text-indigo-400" />
                        <span className="text-[10px] text-slate-500 text-center leading-tight">{label}</span>
                      </div>
                    ))}
                  </div>

                  <p className="text-center text-[11px] text-slate-600 leading-relaxed">
                    Login credentials will be emailed immediately after payment.<br />
                    By enrolling you agree to our{" "}
                    <Link to="/terms" className="text-indigo-400 hover:underline">Terms of Service</Link>.
                  </p>
                </form>
              )}
            </div>
          </div>

          {/* Benefits recap below card */}
          <div className="mt-4 space-y-2">
            {["NCERT notes on every topic", "Unlimited descriptive + MCQ practice", "Question bank with previous year papers", "Bilingual — English + हिंदी"].map((t, i) => (
              <div key={i} className="flex items-center gap-2.5 text-sm text-slate-400">
                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                {t}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────── */}
      <footer className="relative z-10 border-t border-white/5 px-6 md:px-12 py-8 mt-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <BookOpen size={14} className="text-white" />
            </div>
            <span className="text-sm font-bold text-slate-400">Descriptive Test</span>
          </div>
          <p className="text-xs text-slate-600">© 2026 Descriptive Test. All rights reserved. | India's Best TGT/PGT Exam Platform</p>
          <div className="flex gap-4 text-xs text-slate-600">
            <Link to="/terms" className="hover:text-slate-400 transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-slate-400 transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
